/** 5 -> 6: the map carries magic ground.
 *
 * Section 31. `AdventureMapState` gained two fields: `ground`, a slot per cell,
 * and `groundIndex`, the legend that says what those slots mean. A map written
 * before this had neither, and every cell on it was ordinary ground - so the
 * rung writes a zero plane and a legend holding nothing but slot 0, which is
 * exactly what "ordinary ground everywhere" is spelled as.
 *
 * The plane is sized from the header the file already carries rather than from
 * the length of a sibling array: `width * height * levels` is what every other
 * plane in the map is checked against (`scenario.ts`), so a file whose header
 * and planes disagree is a malformed file and is refused by the validator after
 * this rung, not quietly given a plane of the wrong size here.
 */

import type { Migration } from "./types.js";
import { isRecord, readSave, writeSave } from "./tree.js";

const size = (map: Record<string, unknown>): number => {
  const n = (key: string): number => {
    const v = map[key];
    return typeof v === "number" && Number.isInteger(v) && v > 0 ? v : 0;
  };
  return n("width") * n("height") * n("levels");
};

export const migration0006: Migration = {
  from: 5,
  to: 6,
  describe: "give the map a magic-ground plane and its legend",
  migrate(raw: unknown): Record<string, unknown> | null {
    const save = readSave(raw);
    if (save === null) return null;

    const map = save.state["map"];
    if (!isRecord(map)) return writeSave(save, save.state, 6);

    const has = (key: string): boolean => Object.prototype.hasOwnProperty.call(map, key);
    if (has("ground") && has("groundIndex")) return writeSave(save, save.state, 6);

    const next = {
      ...map,
      ...(has("ground") ? {} : { ground: new Uint8Array(size(map)) }),
      ...(has("groundIndex") ? {} : { groundIndex: [null] }),
    };
    return writeSave(save, { ...save.state, map: next }, 6);
  },
};
